import React, { useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import Button from "./Button";

interface LicensePhotoProps {
  photo?: Blob | null;
  className?: string;
  onConfirmPhoto: (photo: Blob) => void;
}

const LicensePhoto = ({
  photo,
  className,
  onConfirmPhoto,
}: LicensePhotoProps) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startCamera = async () => {
    try {
      setLoading(true);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 360 },
          facingMode: "environment",
          aspectRatio: 16 / 9,
          frameRate: { ideal: 30 },
        },
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      setError("Error accessing camera");
    } finally {
      setLoading(false);
    }
  };

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (canvasRef.current) {
      canvasRef.current.getContext("2d")?.clearRect(0, 0, 640, 360);
    }
  }, [stream]);

  // Start camera stream
  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const snapshot = useCallback(async (): Promise<Blob | null> => {
    if (!videoRef.current || !canvasRef.current) return null;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    if (!context) return null;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            resolve(null);
          }
        },
        "image/jpeg",
        0.5,
      );
    });
  }, []);

  const handleConfirmPhoto = useCallback(async () => {
    const photoBlob = photo ?? (await snapshot());
    if (!photoBlob) {
      await startCamera();
      return;
    }

    onConfirmPhoto(photoBlob!);
    stopCamera();
  }, [snapshot, onConfirmPhoto, stopCamera, photo]);

  return (
    <div className={className}>
      <div className="flex flex-col gap-2 aspect-video relative">
        {error && (
          <div className="w-full h-full">
            <div className="text-red">{error}</div>
            <Button onClick={startCamera} className="font-semibold">
              {t("waiver.licenseModal.retry")}
            </Button>
          </div>
        )}

        {loading && (
          <div className="flex justify-center items-center h-full">
            <div className="text-blue">{t("waiver.licenseModal.loading")}</div>
          </div>
        )}
        <div className="absolute w-full h-full border-[0.5px] border-light-gray">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            disablePictureInPicture
            muted
            width={640}
            height={360}
            className="w-full h-auto"
          />
          <canvas ref={canvasRef} style={{ display: "none" }} />
        </div>
      </div>

      <div className="flex flex-col justify-center gap-4 mt-4 px-8">
        <Button
          onClick={handleConfirmPhoto}
          disabled={!stream}
          className="font-semibold"
        >
          {t("waiver.licenseModal.confirm")}
        </Button>
      </div>
    </div>
  );
};

export default LicensePhoto;
